import { useState, useEffect, useRef } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { VoiceToTextTextarea as Textarea } from "@/components/voice-field";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, Mail, Send, CheckCircle, Clock } from "lucide-react";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { useSendEmailWithPreflight } from "@/hooks/use-pending-email-drafts";
import { CcEmailInput } from "@/components/cc-email-input";
import { EmailAttachmentsField, type StagedAttachment } from "@/components/email-attachments-field";
import { ScheduledEmailDialog } from "@/components/scheduled-email-dialog";
import type { UserProfile } from "@shared/schema";

const PAID_DEFAULT_TEMPLATE_KEY = "paid_email_default_template";

interface EmailTemplate {
  id: string;
  name: string;
  subject: string;
  body: string;
  bodyRich?: string | null;
  scope: string;
  ownerUserId?: string | null;
  hiddenFromPickers?: boolean;
}

interface PaymentEmailModalProps {
  open: boolean;
  onClose: () => void;
  invoiceId: string | null;
  clientId: string | null;
  clientEmail: string | null;
  clientName: string | null;
  invoiceNumber: string | null;
}

function stripHtml(html: string): string {
  return html
    .replace(/<\/p>/gi, "\n\n")
    .replace(/<br\s*\/?>/gi, "\n")
    .replace(/<\/div>/gi, "\n")
    .replace(/<\/li>/gi, "\n")
    .replace(/<[^>]+>/g, "")
    .replace(/&nbsp;/g, " ")
    .replace(/&amp;/g, "&")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&quot;/g, '"')
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}

function applyVars(text: string, vars: Record<string, string>): string {
  return Object.entries(vars).reduce((t, [k, v]) => t.replaceAll(k, v), text);
}

export function PaymentEmailModal({
  open,
  onClose,
  invoiceId,
  clientId,
  clientEmail,
  clientName,
  invoiceNumber,
}: PaymentEmailModalProps) {
  const { toast } = useToast();
  const [selectedTemplateId, setSelectedTemplateId] = useState("");
  const [setAsDefault, setSetAsDefault] = useState(false);
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [cc, setCc] = useState("");
  const [attachments, setAttachments] = useState<StagedAttachment[]>([]);
  const [sent, setSent] = useState(false);
  const initRef = useRef(false);

  const { data: profile } = useQuery<UserProfile>({
    queryKey: ["/api/profile"],
  });

  const { data: allTemplates = [] } = useQuery<EmailTemplate[]>({
    queryKey: ["/api/email-templates"],
    enabled: open,
  });

  // Filter to STANDARD (master) + current user's USER templates.
  // Task #696: also drop templates flagged hidden-from-pickers so admin-only
  // automation seeds (e.g. invoice_resend, *_day_reminder) never appear in
  // the payment-email composer.
  const templates = allTemplates.filter(
    (t) =>
      !t.hiddenFromPickers &&
      (t.scope === "STANDARD" ||
        (t.scope === "USER" && t.ownerUserId === profile?.userId))
  );
  const masterTemplates = templates.filter((t) => t.scope === "STANDARD");
  const myTemplates = templates.filter((t) => t.scope === "USER");

  // Load saved default template when opening
  useEffect(() => {
    if (open && !initRef.current) {
      const saved = localStorage.getItem(PAID_DEFAULT_TEMPLATE_KEY);
      if (saved) setSelectedTemplateId(saved);
      setSent(false);
      initRef.current = true;
    }
    if (!open) {
      initRef.current = false;
    }
  }, [open]);

  // Reset sent state and init on open
  useEffect(() => {
    if (open) {
      setSent(false);
    }
  }, [open]);

  // Populate subject and body when a template is selected
  useEffect(() => {
    if (!selectedTemplateId || !templates.length) return;
    const tpl = templates.find((t) => t.id === selectedTemplateId);
    if (!tpl) return;

    const contactName = clientName || "";
    const contactFirst = contactName.split(" ")[0] || "";
    const vars: Record<string, string> = {
      "{{first_name}}": contactFirst,
      "{{contact_name}}": contactName,
      "{{client_name}}": contactName,
      "{{account_name}}": contactName,
      "{{invoice_number}}": invoiceNumber || "",
    };

    setSubject(applyVars(tpl.subject, vars));
    // Always strip HTML — both bodyRich and body can contain HTML tags
    const rawBody = tpl.bodyRich || tpl.body || '';
    const bodyText = stripHtml(rawBody);
    setBody(applyVars(bodyText, vars));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTemplateId, templates.length]);

  const sendEmail = useSendEmailWithPreflight();
  const [isSending, setIsSending] = useState(false);
  const [isScheduleOpen, setIsScheduleOpen] = useState(false);

  const handleSend = async () => {
    if (!clientEmail) return;
    if (setAsDefault && selectedTemplateId) {
      localStorage.setItem(PAID_DEFAULT_TEMPLATE_KEY, selectedTemplateId);
    }

    // Snapshot the values, drop the dialog, then send in the background.
    const payload = {
      to: clientEmail,
      subject,
      body,
      cc: cc.trim() || undefined,
      clientId: clientId || undefined,
      invoiceId: invoiceId || undefined,
      attachments: attachments.length > 0
        ? attachments.map((a) => ({
            filename: a.filename,
            contentBase64: a.contentBase64,
            contentType: a.contentType,
          }))
        : undefined,
    };
    const recipient = clientEmail;
    const sub = subject;
    handleClose();
    setIsSending(true);

    await sendEmail(
      {
        endpoint: "/api/email/custom",
        payload,
        recipientEmail: recipient,
        subject: sub,
        description: "payment confirmation",
      },
      {
        onSent: () => {
          toast({
            title: "Email sent",
            description: `Sent payment confirmation to ${recipient}.`,
          });
        },
        onError: (error: any) => {
          toast({
            title: "Failed to Send",
            description: error?.message || "Could not send email. Please try again.",
            variant: "destructive",
          });
        },
      },
    );
    setIsSending(false);
  };

  const handleClose = () => {
    setSelectedTemplateId("");
    setSetAsDefault(false);
    setSubject("");
    setBody("");
    setCc("");
    setAttachments([]);
    setSent(false);
    onClose();
  };

  if (!clientEmail) return null;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && handleClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Mail className="w-5 h-5 text-primary" />
            Email Event Contact
          </DialogTitle>
          <DialogDescription>
            {sent
              ? "Your email has been sent successfully."
              : `Send a payment confirmation email to the event contact for ${clientName || "this client"}.`}
          </DialogDescription>
        </DialogHeader>

        {sent ? (
          <div className="py-6 flex flex-col items-center gap-3 text-center">
            <CheckCircle className="w-12 h-12 text-green-500" />
            <p className="font-medium">Email sent to {clientEmail}</p>
            <p className="text-sm text-muted-foreground">
              Invoice #{invoiceNumber} — payment confirmation delivered.
            </p>
          </div>
        ) : (
          <div className="space-y-4 py-2">
            {/* Template selector */}
            {templates.length > 0 && (
              <div className="space-y-1.5">
                <Label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                  Choose a Template
                </Label>
                <Select value={selectedTemplateId} onValueChange={setSelectedTemplateId}>
                  <SelectTrigger data-testid="select-payment-email-template">
                    <SelectValue placeholder="Select a template…" />
                  </SelectTrigger>
                  <SelectContent>
                    {myTemplates.length > 0 && (
                      <>
                        <div className="px-2 py-1.5 text-xs font-semibold text-muted-foreground">
                          My Templates
                        </div>
                        {myTemplates.map((t) => (
                          <SelectItem key={t.id} value={t.id} data-testid={`template-option-${t.id}`}>
                            {t.name}
                          </SelectItem>
                        ))}
                      </>
                    )}
                    {masterTemplates.length > 0 && (
                      <>
                        <div className="px-2 py-1.5 text-xs font-semibold text-muted-foreground">
                          {myTemplates.length > 0 ? "Master Templates" : "Templates"}
                        </div>
                        {masterTemplates.map((t) => (
                          <SelectItem key={t.id} value={t.id} data-testid={`template-option-${t.id}`}>
                            {t.name}
                          </SelectItem>
                        ))}
                      </>
                    )}
                  </SelectContent>
                </Select>

                {selectedTemplateId && (
                  <div className="flex items-center gap-2 pt-1">
                    <Checkbox
                      id="paid-set-default"
                      checked={setAsDefault}
                      onCheckedChange={(v) => setSetAsDefault(!!v)}
                      data-testid="checkbox-set-default-payment-template"
                    />
                    <label
                      htmlFor="paid-set-default"
                      className="text-xs text-muted-foreground cursor-pointer select-none"
                    >
                      Set as my default template for paid invoices
                    </label>
                  </div>
                )}
              </div>
            )}

            {/* To */}
            <div className="space-y-1.5">
              <Label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                To
              </Label>
              <Input
                value={clientEmail}
                readOnly
                className="text-sm bg-muted/50"
                data-testid="input-payment-email-to"
              />
            </div>

            {/* CC */}
            <CcEmailInput
              value={cc}
              onChange={setCc}
              data-testid="input-payment-email-cc"
            />

            {/* Subject */}
            <div className="space-y-1.5">
              <Label htmlFor="pay-email-subject" className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                Subject
              </Label>
              <Input
                id="pay-email-subject"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                placeholder="Payment received — thank you!"
                className="text-sm"
                data-testid="input-payment-email-subject"
              />
            </div>

            {/* Body */}
            <div className="space-y-1.5">
              <Label htmlFor="pay-email-body" className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                Message
              </Label>
              <Textarea
                id="pay-email-body"
                value={body}
                onChange={(e) => setBody(e.target.value)}
                placeholder="Write your message…"
                rows={6}
                className="text-sm resize-none"
                data-testid="input-payment-email-body"
              />
            </div>

            <EmailAttachmentsField
              value={attachments}
              onChange={setAttachments}
              testIdPrefix="payment-email-attachments"
            />
          </div>
        )}

        <DialogFooter className="gap-2 flex-col sm:flex-row">
          <Button variant="outline" onClick={handleClose} data-testid="button-payment-email-skip">
            {sent ? "Close" : "Skip"}
          </Button>
          {!sent && (
            <>
              <Button
                variant="outline"
                onClick={() => setIsScheduleOpen(true)}
                disabled={!subject.trim() || !body.trim() || isSending}
                data-testid="button-payment-email-schedule"
              >
                <Clock className="w-4 h-4 mr-2" />
                Schedule send
              </Button>
              <Button
                onClick={handleSend}
                disabled={!subject.trim() || !body.trim() || isSending}
                data-testid="button-payment-email-send"
              >
                {isSending ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Sending…</>
                ) : (
                  <><Send className="w-4 h-4 mr-2" />Send Email</>
                )}
              </Button>
            </>
          )}
        </DialogFooter>
        <ScheduledEmailDialog
          open={isScheduleOpen}
          onOpenChange={setIsScheduleOpen}
          sourceType="one_off"
          recipientSummary={clientEmail ? `Schedule payment confirmation to ${clientEmail}` : undefined}
          subjectSummary={subject}
          buildPayload={() => {
            if (!clientEmail || !subject.trim() || !body.trim()) return null;
            return {
              to: clientEmail,
              cc: cc.trim() || undefined,
              subject,
              htmlBody: body,
              attachments:
                attachments.length > 0
                  ? attachments.map((a) => ({
                      filename: a.filename,
                      contentBase64: a.contentBase64,
                      contentType: a.contentType,
                    }))
                  : undefined,
              clientId: clientId || undefined,
              invoiceId: invoiceId || undefined,
            };
          }}
          onScheduled={() => {
            handleClose();
          }}
        />
      </DialogContent>
    </Dialog>
  );
}
